/**
 * Minimal, anonymous usage counters.
 *
 * Requests a 1x1 GIF served by CloudFront with the event name in the query
 * string. Nothing is stored client-side and no payload is sent — the event is
 * recorded purely by the request appearing in the CloudFront access log.
 *
 * The distribution keeps the query string out of its cache key, so every
 * beacon is a cache hit at the edge: S3 is never touched, but the request is
 * still logged. `cache: 'no-store'` stops the *browser* from short-circuiting
 * repeat beacons so they actually reach the edge.
 */

const BEACON_PATH = '/px.gif';

let pageViewSent = false;

/**
 * Fires a beacon. Never throws and never rejects — analytics must not be able
 * to break the app or surface an error to the user.
 *
 * @param {string} event Short event name, e.g. 'pageview'.
 */
function send(event) {
  if (typeof fetch !== 'function') return;
  try {
    fetch(`${BEACON_PATH}?e=${encodeURIComponent(event)}`, {
      method: 'GET',
      cache: 'no-store',
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ignore — a failed beacon is not worth reporting.
  }
}

/**
 * Records a single visit. Repeat calls within a page lifetime are ignored so
 * StrictMode's double-invoked effects don't double-count.
 */
export function trackPageView() {
  if (pageViewSent) return;
  pageViewSent = true;
  send('pageview');
}

/** Records that the user downloaded their booklet. */
export function trackDownload() {
  send('download');
}
